/**
 * client/src/cma/compLadder.ts — which sale leads the brief, as arithmetic rather than as a drag.
 *
 * =================================================================================================
 * WHY THIS IS A MODULE AND NOT TWENTY LINES INSIDE `ConfirmGrid`
 * =================================================================================================
 * Owner, 2026-09-08: *"we need to make sure that we allow the agent to select the comp that is the
 * primary secondary and 3rd closest comp. we auto rank but the agent can override to make sure that
 * our list price is correct."*
 *
 * Two surfaces have to agree about the answer, and they are in different files:
 *
 *   · `ConfirmGrid` paints the rows — and `CompCards` paints the same rows in the same order,
 *     because both take one index list.
 *   · `CmaAmbFlow` builds the SUBMITTED comparables array, whose order is the order all four
 *     layouts print (`amb_build.py:4080` filters the incoming list and never re-sorts it).
 *
 * And a third thing checks them against each other: `engine/comp_primacy.py` refuses the record
 * when the ladder it was sent does not describe the comparables it was sent beside. That refusal
 * is the safety of the feature, so the screen and the wire must derive the order from ONE
 * function. Two spellings of "lead first, then the rest" is one build away from a brief whose note
 * says 1712 Oak leads and whose first column is somewhere else.
 *
 * =================================================================================================
 * THE RULE THAT MAKES A GAP IMPOSSIBLE
 * =================================================================================================
 * `comp_primacy.validate` refuses a third with no second, on the grounds that it is not an
 * ordering. Rather than let an agent build one and then be told off for it, the ladder is kept
 * contiguous BY CONSTRUCTION:
 *
 *   · `availablePositions` offers only the places that exist yet — one more than are already
 *     taken, capped at three. Nothing set offers "1st" alone; the control teaches the rule by
 *     only ever showing reachable rungs.
 *   · Choosing a place another sale holds SWAPS the two. That is what every ranking control
 *     anybody has used does, and it cannot leave a hole.
 *   · Clearing a rung promotes everything under it. "I dropped the primary, the secondary became
 *     the primary" is what a person expects, and it is the only removal that keeps a ladder.
 *
 * =================================================================================================
 * WHAT ORDERING DOES NOT DO
 * =================================================================================================
 * It does not move the reconciled figure. `WEIGHT_NOTE` says so on the screen, and it is a
 * duplicate of the sentence in `engine/comp_primacy.py` rather than an import, because this file
 * is TypeScript and that one is Python. `tests/test_comp_primacy_sentence_is_one_sentence.py` pins
 * the two together, exactly as `test_field_override.py` pins the three spellings of "Not on file".
 */
import type { CandidateComp } from "./types";

/** How many places on the ladder there are. Mirrors `comp_primacy.MAX_LEAD`. */
export const MAX_LEAD = 3;

/** A place on the ladder. Deliberately a union rather than `number`: an unreachable fourth rung
 *  should be a type error here rather than a refusal from the server. */
export type LeadPosition = 1 | 2 | 3;

/** The words, in the order they are offered. Mirrors `comp_primacy.LEAD_WORDS`. */
export const LEAD_WORDS: Record<LeadPosition, string> = { 1: "primary", 2: "secondary", 3: "third" };

/** What the control shows for each place. Short, because it sits in a table cell. */
export const LEAD_SHORT: Record<LeadPosition, string> = { 1: "1st", 2: "2nd", 3: "3rd" };

/**
 * THE SENTENCE THAT KEEPS THIS FEATURE HONEST — a duplicate of `comp_primacy.WEIGHT_NOTE`.
 *
 * "Primary comparable" sounds like "the one that counts most". It is not: the reconciliation
 * weights each sale by how FEW adjustments it needed, which is the agent's own rule, and position
 * is not an input to it. An agent who moves a sale to the top and watches the price hold still has
 * to be told why before they wonder whether the control is broken.
 */
export const WEIGHT_NOTE =
  "Ordering the comparables sets which sale the brief leads with; it does not change the " +
  "reconciled figure. That is weighted by how few adjustments each sale needed, and it moves " +
  "when you include or exclude a sale, correct an adjustment, or record your own figure.";

/** The place this sale holds, or null. Defensive about anything that is not a real rung. */
export function leadOf(comp: CandidateComp | undefined | null): LeadPosition | null {
  const raw = (comp ?? {})._lead;
  return raw === 1 || raw === 2 || raw === 3 ? raw : null;
}

/**
 * The ladder as it stands: array index by place, `1` first. Only contiguous rungs are returned.
 *
 * TRUNCATED AT THE FIRST HOLE rather than reported with one. Every writer below keeps the ladder
 * contiguous, so a hole means state that arrived from somewhere else (an older draft, a hand-edited
 * store), and the honest reading of "primary and third, no second" is that only the primary is
 * known — not that the third sale is second.
 */
export function ladderIndices(comps: readonly CandidateComp[]): number[] {
  const out: number[] = [];
  for (let pos = 1; pos <= MAX_LEAD; pos += 1) {
    const at = comps.findIndex((c) => leadOf(c) === pos);
    if (at < 0) break;
    out.push(at);
  }
  return out;
}

/**
 * The places the control may offer for one row: every place already taken, plus the next one.
 *
 * The row's OWN place is always included — a select whose current value is not among its options
 * is a control that cannot describe itself.
 */
export function availablePositions(
  comps: readonly CandidateComp[],
  index: number,
): LeadPosition[] {
  const taken = ladderIndices(comps).length;
  const mine = leadOf(comps[index]);
  const reach = Math.min(MAX_LEAD, mine === null ? taken + 1 : Math.max(taken, mine));
  const out: LeadPosition[] = [];
  for (let p = 1; p <= reach; p += 1) out.push(p as LeadPosition);
  return out;
}

/**
 * Set (or clear, with `null`) the place one sale holds, returning a NEW comps array.
 *
 * A pure function over the whole array rather than a patch to one row, because both writes are
 * about a relationship between rows: taking a place swaps with whoever held it, and giving one up
 * promotes everyone below. A one-row patch cannot express either without the caller re-deriving
 * the rule, which is the second implementation this file exists to prevent.
 *
 * THE ARRAY ORDER IS NOT TOUCHED. Every checkbox, every edited cell and the parallel
 * `meta._overrides.comps` index list address a comp by its position in this array; reordering it
 * here would re-point all of them. What changes is one field on at most three rows. The ORDER is
 * derived at the two places that need it — `displayOrder` below — and nowhere else.
 */
export function setLead(
  comps: readonly CandidateComp[],
  index: number,
  position: LeadPosition | null,
): CandidateComp[] {
  const next = comps.map((c) => ({ ...c }));
  const target = next[index];
  if (!target) return comps.map((c) => c);

  const was = leadOf(target);
  if (was === position) return comps.map((c) => c);

  if (position === null) {
    // Clearing a rung: everything under it moves up one, so the ladder stays contiguous.
    delete target._lead;
    if (was !== null) {
      next.forEach((c) => {
        const p = leadOf(c);
        if (p !== null && p > was) c._lead = (p - 1) as LeadPosition;
      });
    }
    return next;
  }

  // Taking a place somebody holds is a SWAP, which is what every ranking control does and is the
  // only resolution that cannot leave a hole where the other sale was.
  const holder = next.find((c) => leadOf(c) === position);
  if (holder && holder !== target) {
    if (was === null) delete holder._lead;
    else holder._lead = was;
  }
  target._lead = position;
  return next;
}

/**
 * WHAT THE ORDER LISTED GIVES EACH TICKED SALE RIGHT NOW (comps-ux, 2026-10-25), as `{index: place}`.
 *
 * The rows arrive in the search's own order (MLS nearest first, then county and public records;
 * `rank_comps` is NOT called on the cascade path), so this is a reading of the order LISTED, not the
 * engine's ranking. The first three ticked sales take the
 * three places. This is a READING of the order, never a write: no `_lead` is set, nothing goes to
 * the server as an override, and the printed order is unchanged until the agent changes a place.
 * With a ladder already set by the agent, the ladder is the answer and this returns it unchanged.
 */
export function effectivePlaces(comps: readonly CandidateComp[]): Map<number, LeadPosition> {
  const out = new Map<number, LeadPosition>();
  const set = ladderIndices(comps);
  if (set.length > 0) {
    set.forEach((at, n) => out.set(at, (n + 1) as LeadPosition));
    return out;
  }
  let next = 1;
  comps.forEach((c, i) => {
    if (c._selected && next <= MAX_LEAD) out.set(i, (next++) as LeadPosition);
  });
  return out;
}

/** How many places a ticked sale may take: one per ticked sale, up to three. */
export function reachablePlaces(comps: readonly CandidateComp[]): LeadPosition[] {
  const n = Math.min(MAX_LEAD, comps.filter((c) => c._selected).length);
  return Array.from({ length: n }, (_, k) => (k + 1) as LeadPosition);
}

/**
 * The agent picks a place for one sale from a surface that SHOWS our ranking (the cards). If no
 * ladder is set yet, our ranking is written down first (`effectivePlaces`), so what the agent saw
 * is what gets rearranged; then the ordinary `setLead` swap runs. No duplicates, no gap: taking a
 * place another sale holds swaps the two, and a sale pushed off the ladder returns to "Auto".
 */
export function setLeadFromRanking(
  comps: readonly CandidateComp[],
  index: number,
  position: LeadPosition | null,
): CandidateComp[] {
  let base: CandidateComp[] = comps.map((c) => ({ ...c }));
  if (ladderIndices(base).length === 0 && position !== null) {
    effectivePlaces(base).forEach((place, at) => { base[at]._lead = place; });
  }
  return setLead(base, index, position);
}

/**
 * THE PLACES AS A COMPLETE LIST (card number buttons, 2026-11-03): the ticked sales in the order
 * the report will use them, as indices into `comps`, at most `MAX_LEAD` long.
 *
 * It is the ladder as it stands (`effectivePlaces`), restricted to ticked sales, then topped up with
 * the next ticked sales in the order listed until three places are filled. Topping up matters: with
 * two rungs set and a third sale ticked later, that third sale reads "Auto" and has no rung, and a
 * step that moved the second sale down would write a third place with no second -- the hole
 * `setLead`'s own comment says the ladder must never have.
 */
export function placedIndices(comps: readonly CandidateComp[]): number[] {
  const out = [...effectivePlaces(comps).entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([i]) => i)
    .filter((i) => !!comps[i]?._selected);
  comps.forEach((c, i) => {
    if (c._selected && out.length < MAX_LEAD && !out.includes(i)) out.push(i);
  });
  return out.slice(0, MAX_LEAD);
}

/** Why a sale cannot take a step, in words, or null when it can. The same sentence is the button's
 *  accessible name and its visible note, so the reason is where the control would have been. */
export function stepRefusal(
  comps: readonly CandidateComp[],
  index: number,
  direction: -1 | 1,
): string | null {
  const placed = placedIndices(comps);
  const at = placed.indexOf(index);
  if (at < 0) return "Include this sale to give it a place.";
  if (placed.length < 2) return "Only one sale is included, so there is nothing to reorder.";
  if (direction < 0 && at === 0) return "Already first in the report.";
  if (direction > 0 && at === placed.length - 1) {
    return placed.length < MAX_LEAD
      ? `Already last: only ${placed.length} sales are included.`
      : `Already last: the report uses up to ${MAX_LEAD} sales.`;
  }
  return null;
}

/**
 * Move one ticked sale one place up (`-1`) or down (`+1`) and return a NEW comps array.
 *
 * The neighbour it passes takes its old place, so the places stay 1..n, each held once. Nothing
 * outside the ladder is touched but the `_lead` mark on at most `MAX_LEAD` rows: array order,
 * ticks, edits and the override trail are exactly as they were (see `setLead`). A step that has
 * nowhere to go returns a copy that is deep-equal to the input.
 */
export function stepLead(
  comps: readonly CandidateComp[],
  index: number,
  direction: -1 | 1,
): CandidateComp[] {
  if (stepRefusal(comps, index, direction) !== null) return comps.map((c) => c);
  const placed = placedIndices(comps);
  const at = placed.indexOf(index);
  [placed[at], placed[at + direction]] = [placed[at + direction], placed[at]];
  const next = comps.map((c) => ({ ...c }));
  next.forEach((c) => { delete c._lead; });
  placed.forEach((i, n) => { next[i]._lead = (n + 1) as LeadPosition; });
  return next;
}

/**
 * Move one ticked sale straight to `place`; the sales between shift one place to keep 1..n
 * contiguous (3 -> 1 makes the old 1 and 2 into 2 and 3). The current place, a place that does not
 * exist for the ticked set, or a sale with no place returns an equal copy. Same `_lead` ladder and
 * same rules as `stepLead`: only the marks change.
 */
export function moveLead(
  comps: readonly CandidateComp[],
  index: number,
  place: number,
): CandidateComp[] {
  const placed = placedIndices(comps);
  const at = placed.indexOf(index);
  if (at < 0 || !Number.isInteger(place) || place < 1 || place > placed.length || place - 1 === at) {
    return comps.map((c) => c);
  }
  placed.splice(at, 1);
  placed.splice(place - 1, 0, index);
  const next = comps.map((c) => ({ ...c }));
  next.forEach((c) => { delete c._lead; });
  placed.forEach((i, n) => { next[i]._lead = (n + 1) as LeadPosition; });
  return next;
}

/** Clear the whole ladder — "put them back the way the analysis ranked them". */
export function clearLadder(comps: readonly CandidateComp[]): CandidateComp[] {
  return comps.map((c) => {
    if (leadOf(c) === null) return c;
    const copy = { ...c };
    delete copy._lead;
    return copy;
  });
}

/**
 * THE ORDER, AS INDICES INTO `comps` — the one derivation both the screen and the wire use.
 *
 * `rest` is the order the rows would otherwise be in: the engine's ranking, or whatever the agent
 * sorted a column by. The ladder is placed in front of it and the remaining rows follow in that
 * same order, so sorting a column still does what it always did to everything the agent has not
 * spoken for.
 */
export function displayOrder(
  comps: readonly CandidateComp[],
  rest: readonly number[],
): number[] {
  const lead = ladderIndices(comps);
  if (lead.length === 0) return rest.slice();
  const claimed = new Set(lead);
  return [...lead, ...rest.filter((i) => !claimed.has(i))];
}

/** One rung of the record the server validates. Mirrors `comp_primacy`'s rung shape exactly. */
export interface LeadRung {
  position: LeadPosition;
  name: string;
  /** Where the analysis had this sale, 1-based, in the order the rows arrived. */
  was: number;
}

/**
 * The record to send beside the reordered comparables.
 *
 * `submitted` is the array the payload will carry, IN THE ORDER IT WILL CARRY IT, and `was` is
 * read off `autoRank` — the order the rows arrived in from the server, which on the cascade path is the
 * search's merge order (not `rank_comps`). Both are passed in rather than recomputed here, because the one thing this record must
 * never do is describe an order the payload does not have.
 *
 * Returns `null` when the agent set no ladder, so the caller can omit the key entirely. An empty
 * overlay and no overlay are the same thing to the engine, and a key that is always present makes
 * a stored payload look like the agent answered a question they were never asked.
 */
export function primacyRecord(
  submitted: readonly CandidateComp[],
  autoRank: readonly CandidateComp[],
  reason?: string,
): { lead: LeadRung[]; reason?: string } | null {
  const lead: LeadRung[] = [];
  for (let pos = 1; pos <= MAX_LEAD; pos += 1) {
    const comp = submitted[pos - 1];
    if (!comp || leadOf(comp) !== pos) break;
    lead.push({
      position: pos as LeadPosition,
      name: String(comp.name ?? ""),
      was: autoRank.indexOf(comp) + 1,
    });
  }
  if (lead.length === 0) return null;
  const stated = (reason ?? "").trim();
  return stated ? { lead, reason: stated } : { lead };
}
